import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { KollelDtoIn } from '@/models/in/KollelDtoIn';
import { CreateKollelFolderDtoIn, KollelFolderDtoIn } from '@/models/in/KollelFolderDtoIn';
import { SupportedAdditionalInfoDtoIn } from '@/models/in/SupportedAdditionalInfoDtoIn';
import { formatCurrencyShort } from '@/helpers/currencyHelper';

interface NewKollelFolderDialogProps {
  fundId?: number;
  kollel: KollelDtoIn;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface AvrechRowState {
  included: boolean;
  paymentInfoId: number;
  excellenceAmount: number | '';
  examsAmount: number | '';
  punctualityAmount: number | '';
}

const emptyRow: AvrechRowState = { included: false, paymentInfoId: 0, excellenceAmount: '', examsAmount: '', punctualityAmount: '' };

// Local month, not toISOString() (UTC): in Israel the first hours of a month are still the
// previous month in UTC, and the form would open on the wrong month.
const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * One avrech's line in the folder builder - a separate component (not inlined in the parent's
 * map) so useGetAuthApi, which fetches THIS avrech's own bank accounts, is one hook call per
 * mounted row rather than a hook called inside a loop (HAT-1446 phase 6). There is no "default"
 * payment info anywhere in this codebase - the coordinator must always pick one explicitly, same
 * as the ordinary single-person support-request flow.
 */
const AvrechFolderRow = ({
  fundId,
  supportedId,
  name,
  kollel,
  row,
  onChange,
}: {
  fundId?: number;
  supportedId: number;
  name: string;
  kollel: KollelDtoIn;
  row: AvrechRowState;
  onChange: (next: AvrechRowState) => void;
}) => {
  const { t } = useTranslation();
  const { data: additionalInfo } = useGetAuthApi<SupportedAdditionalInfoDtoIn>(
    fundId && row.included ? `funds/${fundId}/supporteds/${supportedId}/additional-info` : '',
  );
  const paymentInfos = additionalInfo?.paymentInfos ?? [];

  const update = (patch: Partial<AvrechRowState>) => onChange({ ...row, ...patch });

  const total = (Number(row.excellenceAmount) || 0) + (Number(row.examsAmount) || 0) + (Number(row.punctualityAmount) || 0);

  const amountField = (
    field: 'excellenceAmount' | 'examsAmount' | 'punctualityAmount',
    labelKey: string,
    limit: number | null,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="body-xs-regular text-(--font-secondary)">{t(labelKey)}</span>
      <input
        type="number"
        min={0}
        max={limit ?? undefined}
        disabled={!row.included}
        className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular w-full disabled:opacity-50"
        value={row[field]}
        onChange={(e) => update({ [field]: e.target.value === '' ? '' : Number(e.target.value) })}
      />
    </label>
  );

  return (
    <div className="flex flex-col gap-3 border border-(--border-main) rounded-lg p-3">
      <div className="flex items-center gap-3">
        <Checkbox checked={row.included} onCheckedChange={(checked) => update({ included: checked === true })} />
        <span className="body-sm-semibold flex-1">{name}</span>
        <span className="body-sm-regular text-(--font-secondary)">{formatCurrencyShort(total)}</span>
      </div>

      {row.included && (
        <div className="grid grid-cols-4 gap-3">
          <label className="flex flex-col gap-1">
            <span className="body-xs-regular text-(--font-secondary)">{t('kollels.folders.form.paymentInfoLabel')}</span>
            <select
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={row.paymentInfoId}
              onChange={(e) => update({ paymentInfoId: Number(e.target.value) })}
            >
              <option value={0} disabled>
                {t('kollels.folders.form.selectPaymentInfo')}
              </option>
              {paymentInfos.map((pi) => (
                <option key={pi.id} value={pi.id}>
                  {t('bank')} {pi.bankNumber}
                  {pi.bankName ? ` (${pi.bankName})` : ''} | {t('branch')} {pi.branchNumber} | {t('account')} {pi.accountNumber}
                </option>
              ))}
            </select>
          </label>
          {amountField('excellenceAmount', 'kollels.form.excellenceLimitLabel', kollel.excellenceLimit)}
          {amountField('examsAmount', 'kollels.form.examsLimitLabel', kollel.examsLimit)}
          {amountField('punctualityAmount', 'kollels.form.punctualityLimitLabel', kollel.punctualityLimit)}
        </div>
      )}
    </div>
  );
};

/**
 * Builds one month's consolidated stipend request for a kollel (HAT-1446 phase 6): the coordinator
 * picks a month, opts avrechim in one at a time, and sets each one's three stipend amounts and
 * bank account. Per-category amounts are capped client-side at the kollel's own
 * Excellence/Exams/PunctualityLimit (when set) - the server re-checks the same caps.
 *
 * Only reachable once the kollel itself is welfare-committee Approved - the parent (KollelFoldersDialog)
 * does not render the "+ בקשה חדשה" button otherwise.
 */
const NewKollelFolderDialog = ({ fundId, kollel, open, onClose, onSuccess }: NewKollelFolderDialogProps) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(currentMonthValue());
  const [rows, setRows] = useState<Record<number, AvrechRowState>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMonth(currentMonthValue());
      setRows(Object.fromEntries(kollel.avrechs.map((a) => [a.supportedId, { ...emptyRow }])));
      setError(null);
    }
  }, [open, kollel]);

  const createMutation = usePostAuthApi<KollelFolderDtoIn, CreateKollelFolderDtoIn>(
    fundId ? `funds/${fundId}/kollels/${kollel.id}/folders` : '',
  );

  const includedRows = Object.entries(rows).filter(([, row]) => row.included);

  const handleSubmit = async () => {
    setError(null);

    if (includedRows.length === 0) {
      setError(t('kollels.folders.form.noAvrechsSelected'));
      return;
    }
    if (includedRows.some(([, row]) => !row.paymentInfoId)) {
      setError(t('kollels.folders.form.missingPaymentInfo'));
      return;
    }
    if (includedRows.some(([, row]) => (Number(row.excellenceAmount) || 0) + (Number(row.examsAmount) || 0) + (Number(row.punctualityAmount) || 0) <= 0)) {
      setError(t('kollels.folders.form.missingAmount'));
      return;
    }

    const payload: CreateKollelFolderDtoIn = {
      requestedForMonth: `${month}-01`,
      items: includedRows.map(([supportedId, row]) => ({
        supportedId: Number(supportedId),
        paymentInfoId: row.paymentInfoId,
        excellenceAmount: Number(row.excellenceAmount) || 0,
        examsAmount: Number(row.examsAmount) || 0,
        punctualityAmount: Number(row.punctualityAmount) || 0,
      })),
    };

    try {
      await createMutation.mutateAsync(payload);
      onSuccess();
      onClose();
    } catch {
      setError(t('kollels.folders.form.errorSaving'));
    }
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()} title={t('kollels.folders.form.title')}>
      <div className="flex flex-col gap-4 p-4 max-h-[70vh] overflow-y-auto">
        <label className="flex flex-col gap-1 max-w-[200px]">
          <span className="body-sm-semibold">{t('kollels.folders.form.monthLabel')}</span>
          <input
            type="month"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </label>

        <div className="flex flex-col gap-3">
          {kollel.avrechs.map((avrech) => (
            <AvrechFolderRow
              key={avrech.supportedId}
              fundId={fundId}
              supportedId={avrech.supportedId}
              name={`${avrech.firstName ?? ''} ${avrech.lastName ?? ''}`.trim()}
              kollel={kollel}
              row={rows[avrech.supportedId] ?? emptyRow}
              onChange={(next) => setRows((prev) => ({ ...prev, [avrech.supportedId]: next }))}
            />
          ))}
        </div>

        {error && <p className="body-sm-regular text-(--color-danger)">{error}</p>}
      </div>

      <div className="flex items-center justify-end gap-3 p-4 border-t border-(--border-main)">
        <Button variant="outline" onClick={onClose}>
          {t('kollels.form.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={createMutation.isPending}>
          {createMutation.isPending ? t('kollels.form.saving') : t('kollels.folders.form.submit')}
        </Button>
      </div>
    </MainDialogLayout>
  );
};

export default NewKollelFolderDialog;
